import React, { useContext, useEffect, useState } from 'react';
import styles from './styles/ScenarioComments.module.css';
import axios from 'axios';
import AuthContext from '../../../context/AuthContext';
import deleteImage from '../../../images/delete.png';
import favoriteOutline from '../../../images/FavoriteOutline.png';
import favorite from '../../../images/Favorite.png';

const ScenarioComments = ({ scenarioId, userId }) => {
    const { user } = useContext(AuthContext);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [likedComments, setLikedComments] = useState([]);
    const [likesCount, setLikesCount] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!scenarioId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        axios.get(`${process.env.REACT_APP_API_URL_LOCAL}/api/comment/scenario/${scenarioId}`)
            .then(response => {
                if (Array.isArray(response.data)) {
                    setComments(response.data);
                    const initialLikesCount = {};
                    response.data.forEach(comment => {
                        initialLikesCount[comment._id] = comment?.likers?.length || 0;
                    });
                    setLikesCount(initialLikesCount);
                } else {
                    console.warn('Received non-array data for comments:', response.data);
                    setComments([]);
                    setLikesCount({});
                }
            })
            .catch(error => {
                console.error('Error fetching comments:', error);
                setError('Failed to load comments.');
                setComments([]);
                setLikesCount({});
            })
            .finally(() => {
                setIsLoading(false);
            });

        if (user && Array.isArray(user.likedComments)) {
            setLikedComments(user.likedComments);
        } else {
            setLikedComments([]);
        }

    }, [scenarioId, user]);

    const handleCreateComment = async (e) => {
        e.preventDefault();
        if (!userId || !newComment.trim()) {
            return;
        }
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL_LOCAL}/api/comment`, {
                message: newComment,
                sender: userId,
                origin: scenarioId,
            });
    
            const newCommentData = response.data;
    

            if (newCommentData && !newCommentData.sender && user) {
                 console.warn("Comment response did not include sender after population attempt, using context user details as fallback.");
                 newCommentData.sender = {
                    _id: userId,
                    username: user.username,
                    profilePicture: user.profilePicture,
                 };
            } else if (newCommentData && !newCommentData.sender) {
                 console.error("Comment response missing sender and no context user available.");
                 newCommentData.sender = { _id: userId, username: "[Unknown User]" };
            }
    
            setComments(prevComments => [newCommentData, ...prevComments]);
            setLikesCount(prevLikes => ({
                ...prevLikes,
                [newCommentData._id]: 0,
            }));
            setNewComment('');
        } catch (error) {
            console.error('Error submitting comment:', error);
        }
    };


    const handleClickLike = async (commentId) => {
        if (!userId) return;

        const isCurrentlyLiked = likedComments.includes(commentId);
        const currentLikes = likesCount[commentId] || 0;

        
        setLikedComments(prevLiked => {
            if (isCurrentlyLiked) {
                return prevLiked.filter(id => id !== commentId);
            } else {
                return [...prevLiked, commentId];
            }
        });
        setLikesCount(prevCounts => ({
            ...prevCounts,
            [commentId]: isCurrentlyLiked ? currentLikes - 1 : currentLikes + 1,
        }));

        try {
            await axios.patch(`${process.env.REACT_APP_API_URL_LOCAL}/api/comment/like/${commentId}`, {
                userId: userId
            });

        } catch (error) {
            console.error('Error liking comment: ', error);
            setLikedComments(prevLiked => {
                if (isCurrentlyLiked) {
                    return prevLiked.includes(commentId) ? prevLiked : [...prevLiked, commentId];
                } else {
                    return prevLiked.filter(id => id !== commentId);
                }
            });
            setLikesCount(prevCounts => ({
                ...prevCounts,
                [commentId]: currentLikes,
            }));
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!userId) return; 

        // Optional: confirmation dialog
        // if (!window.confirm("Are you sure you want to delete this comment?")) {
        //     return;
        // }
        

        const originalComments = [...comments];
        const originalLikesCount = { ...likesCount };

        
        setComments(prevComments => prevComments.filter(comment => comment._id !== commentId));
        setLikesCount(prevCounts => {
            const { [commentId]: _, ...rest } = prevCounts;
            return rest;
        });


        try {
            await axios.delete(`${process.env.REACT_APP_API_URL_LOCAL}/api/comment/${commentId}`);
        } catch (error) {
            console.error('Error deleting comment: ', error);
            setComments(originalComments);
            setLikesCount(originalLikesCount);
        }
    };

    

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.spinner}>
                    <div className={styles.dot}></div>
                    <div className={styles.dot}></div>
                    <div className={styles.dot}></div>
                </div>
                <p className={styles.loadingText}>Loading<span className={styles.animatedEllipsis}></span></p>
            </div>
        );
    }

    if (error) {
        return <div className={styles.container}><p className={styles.error}>{error}</p></div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.title}>Comments ({comments.length})</div>

            {/* Only show comment form if user is logged in */}
            {userId && (
                <form onSubmit={handleCreateComment} className={styles.commentForm}>
                    <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className={styles.textarea}
                        minLength={2}
                        placeholder="Add a comment..."
                        required 
                    />
                    <button type="submit" className={styles.submitButton} disabled={!newComment.trim()}>Submit</button>
                </form>
            )}
            {!userId && (
                <p className={styles.loginPrompt}>Log in to post comments and like.</p>
            )}


            <div className={styles.commentsList}>
                {[...comments]
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .map(comment => {
                        const senderUsername = comment.sender?.username ?? "[Deleted User]";
                        const senderId = comment.sender?._id;
                        const isOwnComment = userId && senderId === userId;
                        const isLiked = userId && likedComments.includes(comment._id);

                        return (
                            <div key={comment._id} className={styles.comment}>

                                <div className={styles.messageDeleteModule}>
                                    <div className={styles.message}>{comment.message}</div>
                                    {isOwnComment && (
                                        <img
                                            src={deleteImage}
                                            alt="Delete comment"
                                            onClick={() => handleDeleteComment(comment._id)}
                                            className={styles.deleteButton}
                                            title="Delete comment"
                                        />
                                    )}
                                </div>
                                <div className={styles.userandlike}>
                                    <div className={styles.userInfo}>
                                        <div className={styles.username}>{senderUsername}</div>
                                        <div className={styles.timestamp}>{comment.createdAt ? new Date(comment.createdAt).toLocaleString() : 'No date'}</div>
                                    </div>
                                    <div className={styles.likesModule}>
                                        <img
                                            className={`${styles.likesImage} ${!userId ? styles.disabled : ''}`}
                                            src={isLiked ? favorite : favoriteOutline}
                                            alt={isLiked ? "Unlike" : "Like"}
                                            onClick={userId ? () => handleClickLike(comment._id) : undefined}
                                            title={userId ? (isLiked ? "Unlike" : "Like") : "Log in to like"}
                                        />
                                        <div className={styles.likes}>{likesCount[comment._id] || 0}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                {!isLoading && comments.length === 0 && <p>No comments yet.</p>}
            </div>
        </div>
    );
};

export default ScenarioComments;